import { useState } from "react";

import {
  Alert,
  Displayer,
  EditableInput,
  Modal,
  UploadFileProps,
} from "@smart/itops-sb-design-system-dom";

import {
  AutoFillStatus,
  FieldItem,
  LookupOptions,
  SectionItem,
  TeamItem,
} from "../../types";
import { IntakeAutoFill } from "../autofill";
import { FileField, FileFieldValue } from "../field-components";

export type IntakeFormAutoFillProps = {
  team: TeamItem;
  autoFillSection: SectionItem | undefined;
  sidebar: boolean;
  setSidebar: (sidebar: boolean) => void;
  description?: string;
  disabled?: boolean;
  autoFillStatus?: AutoFillStatus;
  onSubmit?: (uploadedFileKeys: string[]) => Promise<void>;
  onSkip: () => void;
  onReset: () => void;
} & {
  fileLookup: LookupOptions["fileLookup"];
  uploadFileProps: UploadFileProps;
  isFileUploadDisabled?: boolean;
};

export const IntakeFormAutoFill = ({
  team,
  autoFillSection,
  description,
  sidebar,
  setSidebar,
  disabled,
  autoFillStatus,
  fileLookup,
  uploadFileProps,
  onSkip,
  onSubmit,
  onReset,
  isFileUploadDisabled,
}: IntakeFormAutoFillProps) => {
  const [uploadedFiles, setUploadedFiles] = useState<FileFieldValue[]>([]);
  const [confirming, setConfirming] = useState(false);

  if (!autoFillSection) return null;

  const autoFillField: FieldItem = {
    uri: "aiFillFieldUri",
    sectionUri: autoFillSection.uri,
    label: " ",
    description: "",
    order: "a",
    type: "file",
  };

  const isUploading = uploadedFiles.some((f) => f.uploadStatus === "uploading");
  const someUploaded = uploadedFiles.some((f) => f.uploadStatus === "uploaded");

  const onRunAutoFill = () => {
    if (!onSubmit) return;

    const uploadedFileKeys = uploadedFiles
      .filter((uf) => uf.uploadStatus === "uploaded" && uf.key)
      .map((uf) => uf.key!);

    if (uploadedFileKeys.length > 0) {
      onSubmit(uploadedFileKeys).catch((e) =>
        console.error("Something went wrong!", e),
      );
    }
  };

  const onSkipAutoFill = () => {
    setUploadedFiles([]);
    onSkip();
  };

  const shouldLock =
    autoFillStatus === "filling" || autoFillStatus === "completed";

  const supportedFileTypes = [
    { name: "jpeg", extension: ".jpeg" },
    { name: "jpeg", extension: ".jpg" },
    { name: "pdf", extension: ".pdf" },
    { name: "png", extension: ".png" },
    { name: "doc", extension: ".doc" },
    { name: "docx", extension: ".docx" },
  ];

  return (
    <IntakeAutoFill
      team={team}
      onSubmit={() => setConfirming(true)}
      onSkip={onSkipAutoFill}
      sidebar={sidebar}
      setSidebar={setSidebar}
      isUploading={isUploading}
      someUploaded={someUploaded}
      disabled={disabled}
      autoFillStatus={autoFillStatus}
    >
      <div className="flex flex-col gap-xLarge mb-[5.6rem]">
        <div className="[&>h1]:text-h1 [&>h1]:font-semibold [&>h1]:font-poppins">
          <Displayer
            paragraphTag="h1"
            value={autoFillSection.title}
            fallback="Autofill Assistant"
          />
        </div>
        {isFileUploadDisabled && (
          <Alert
            variant="section"
            palette="warning"
            content="This feature is only available for your client. Click next to continue."
          />
        )}
        <EditableInput text={description} fontSize="ui" disabled />
        <FileField
          field={autoFillField}
          value={uploadedFiles}
          onChange={(files) => {
            onReset();
            setUploadedFiles(files);
          }}
          onBlur={() => {}}
          fileLookup={fileLookup}
          fileSizeLimitMegaByte={5}
          supportedFileTypes={supportedFileTypes}
          uploadFileProps={uploadFileProps}
          hideLabel
          disabled={disabled || shouldLock || isFileUploadDisabled}
        />
      </div>
      <Modal
        open={!!confirming}
        onClose={() => setConfirming(false)}
        header={{ text: "Confirm", icon: "regularCircleInfo" }}
        footer={{
          buttons: [
            {
              text: "Cancel",
              variant: "secondaryDefault",
              onClick: () => setConfirming(false),
            },
            {
              text: "Confirm",
              variant: "primaryDefault",
              onClick: () => {
                setConfirming(false);
                onRunAutoFill();
              },
            },
          ],
        }}
      >
        <p>
          Before clicking &apos;Confirm&apos;, ensure all relevant documents are
          uploaded.
        </p>
        <p>
          Once confirmed, we will auto-populate the form, and no further uploads
          or use of the &apos;Autofill Assistant&apos; will be possible.
        </p>
      </Modal>
    </IntakeAutoFill>
  );
};
